import { connect } from 'react-redux'
import { Component } from 'react'
import PropTypes from 'prop-types'
import styled from 'astroturf/react'
import { FieldError, FieldLabel, Fieldset } from '@calendly/form/parts'
import { difference } from 'lodash-es'

import TextField from 'components/form/text'
import CheckboxField from 'components/form/checkbox'
import RadioButtonField from 'components/form/radio_button'
import { currentInviteeSelector } from 'selectors/current_invitee'
import { withI18n } from 'locales'
import _ChoiceTextField from "index-ChoiceTextField.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/form/choice/index.js?styleId=ChoiceTextField";
import _Option from "index-Option.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/form/choice/index.js?styleId=Option";


const ChoiceTextField = /*#__PURE__*/styled(TextField, null, {
  displayName: "ChoiceTextField",
  styles: _ChoiceTextField
})

const Option = /*#__PURE__*/styled('div', null, {
  displayName: "Option",
  styles: _Option
})

class ChoiceField extends Component {
  static propTypes = {
    className: PropTypes.string,
    label: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['one', 'many']).isRequired,
    values: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectedItems: PropTypes.string,
    includeCustomOption: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
    onFocus: PropTypes.func,
    error: PropTypes.string,
    customResponse: PropTypes.string,
    i18n: PropTypes.object.isRequired,
  }

  static defaultProps = {
    className: null,
    selectedItems: '',
    onFocus: () => {},
    error: ''
  }

  constructor(props) {
    super(props)
    this.choiceBoxType = this.getChoiceBoxType()
    this.selectedValues = this.getSelectedValues()
    this.state = { options: this.buildOptions() }
  }

  componentDidMount = () => (this.props.onChange(this.getValue()))

  onChangeChoiceInput(isChecked, key) {
    this.props.onFocus()
    this.setChecked(key, isChecked)
  }

  onFocusTextInput = () => (this.props.onFocus())

  onChangeTextInput(element, key) {
    this.props.onFocus()
    this.setValue(key, element.value)
  }

  render = () => {
    const error = this.props.error
    const errorId = error ? `${this.props.name}-error` : undefined

    return (
      <Fieldset
        className={this.props.className}
        data-component="choice"
        role={this.choiceBoxType === 'radio' ? 'radiogroup' : undefined}
        aria-required={this.choiceBoxType === 'radio' ? this.props.required : undefined}
        aria-invalid={Boolean(error)}
        aria-describedby={errorId}
      >
        {this.renderLabel()}
        {this.renderOptions()}
        <FieldError text={error} id={errorId} />
      </Fieldset>
    )
  }

  renderLabel() {
    return (
      <legend>
        <FieldLabel text={this.props.label} isRequired={this.props.required} />
      </legend>
    )
  }

  renderOptions = () => (
    this.state.options.map((option) => this.renderOption(option))
  )

  renderOption = (option) => (
    <Option key={option.key} data-component="option">
      {this.renderOptionChoiceInput(option)}
      {this.renderOptionTextInput(option)}
    </Option>
  )

  renderOptionChoiceInput(option) {
    const onChange = (isChecked) => this.onChangeChoiceInput(isChecked, option.key)

    const component = (type) => ({
      radio: (
        <RadioButtonField
          name={this.props.name}
          label={option.type === 'defined' ? option.value : undefined}
          aria-label={option.type === 'defined' ? undefined : this.props.i18n.t('booking.templates.other_option')}
          value={option.value}
          checked={option.selected}
          onChange={onChange}
          hasError={!!this.props.error}
          data-testid={option.value}
        />
      ),
      checkbox: (
        <CheckboxField
          name={this.props.name}
          label={option.type === 'defined' ? option.value : undefined}
          aria-label={option.type === 'defined' ? undefined : this.props.i18n.t('booking.templates.other_option')}
          value={option.value}
          checked={option.selected}
          onChange={onChange}
          hasError={!!this.props.error}
        />
      )
    })[type]
    return component(this.choiceBoxType)
  }

  renderOptionTextInput(option) {
    if (option.type !== 'custom') { return false }

    const onFocus = (event) => this.onFocusTextInput(event.target, option.key)

    const onChange = (event) => this.onChangeTextInput(event.target, option.key)

    return (
      <ChoiceTextField
        name={this.props.name}
        placeholder={this.props.i18n.t('booking.templates.other_option')}
        value={option.value}
        onFocus={onFocus}
        onChange={onChange}
        autoComplete="off"
      />
    )
  }

  setChecked(key, checked) {
    const options = this.state.options.map((option) => {
      if (option.key === key) {
        return {
          ...option,
          selected: checked
        }
      } if (this.props.type === 'one') {
        return {
          ...option,
          selected: false
        }
      }
      return option
    })
    this.setStateOptions(options)
  }

  setValue(key, value) {
    const options = this.state.options.map((option) => {
      if (option.key === key) {
        return {
          ...option,
          value,
          selected: true
        }
      }
      return option
    })
    this.setStateOptions(options)
  }

  setStateOptions = (options) => (
    this.setState({ options }, () => this.props.onChange(this.getValue()))
  )

  buildOptions() {
    const options = []
    this.props.values.forEach((value, index) => options.push({
      key: `option-${index}`,
      type: 'defined',
      selected: this.selectedValues.includes(value),
      value
    }))
    if (this.props.includeCustomOption) {
      options.push({
        key: 'option-custom',
        type: 'custom',
        selected: this.props.customResponse !== '',
        value: this.props.customResponse
      })
    }
    return options
  }

  getSelectedValues() {
    const { selectedItems, values } = this.props
    if (!selectedItems) { return [] }
    const selectedValues = selectedItems.split(',').map((valueIndex) => values[parseInt(valueIndex, 10) - 1])
    return (this.props.type === 'one'
      ? selectedValues.slice(0, 1)
      : selectedValues
    )
  }

  getChoiceBoxType() {
    const inputType = (type) => ({
      one: 'radio',
      many: 'checkbox'
    })[type]
    return inputType(this.props.type)
  }

  getValue() {
    return this.state.options
      .filter((option) => option.selected)
      .map((option) => option.value)
      .join('\n')
  }
}

const getCustomResponse = (customFields, ownProps) => {
  if (!customFields) return ''

  const customField = customFields.find((field) => field.question === ownProps.label || field.question === ownProps.name)

  if (!customField) return ''

  const availableOptions = ownProps.values

  const selectedOptions = ownProps.type === 'many'
    ? customField.answer.split('\n')
    : [customField.answer]

  return difference(selectedOptions, availableOptions).join(', ')
}
const mapStateToProps = (state, ownProps) => ({
  customResponse: getCustomResponse(currentInviteeSelector(state)?.questions_and_answers, ownProps)
})

export default connect(mapStateToProps)(withI18n(ChoiceField))
